import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Smartphone, 
  CheckCircle2, 
  MonitorPlay, 
  Clapperboard, 
  Mic, 
  Layers, 
  Globe, 
  Users, 
  Briefcase, 
  Zap, 
  ArrowRight,
  ArrowLeft
} from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "sonner";
import logo from '../assets/images/logo-artie.png';

export default function Onboarding2() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuthStore();
  
  const [videoForm, setVideoForm] = useState<string>(user?.video_form || "Vertical Video");
  const [genre, setGenre] = useState<string>(user?.genre || "Entertainment");
  const [audience, setAudience] = useState<string[]>(
    Array.isArray(user?.audience)
      ? user.audience
      : user?.audience
        ? [user.audience]
        : []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleAudience = (option: string) => {
    setAudience(prev =>
      prev.includes(option)
        ? prev.filter(a => a !== option)
        : [...prev, option]
    );
  };

  const handleSubmit = async () => {
    if (!videoForm) {
      toast.error("Please select what you primarily create");
      return;
    }
    if (!genre) {
      toast.error("Please select what your content focuses on");
      return;
    }
    if (audience.length === 0) {
      toast.error("Please describe your audience");
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.updateCurrentUser({
        video_form: videoForm,
        genre: genre,
        audience: audience,
      });

      await checkAuth();
      toast.success("Preferences saved!");
      navigate("/onboarding/3");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update preferences");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center">

      {/* Top Navigation */}
      <nav className="w-full max-w-5xl px-6 py-6 flex items-center justify-between z-50 relative">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:border-[oklch(0.2686_0_0)] transition-all"
          >
            <ArrowLeft className="w-5 h-5 stroke-[1.5]" />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.70_0.18_55)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
            <div className="w-2 h-2 rounded-full bg-[oklch(0.92_0_0)]"></div>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-xs font-medium text-[oklch(0.2686_0_0)] hover:text-[oklch(0.70_0.18_55)] transition-colors"
          >
            Skip
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 z-10 flex flex-col w-full max-w-2xl pt-8 pr-6 pb-32 pl-6 items-center">
        {/* Header - Artie Guidance */}
        <div className="w-full max-w-lg mb-10 animate-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-start gap-5 bg-white p-6 rounded-2xl border border-[oklch(0.92_0_0)] shadow-sm">
             <div className="hidden sm:block shrink-0">
               <div className="w-16 h-16 rounded-full bg-[oklch(0.97_0.01_60)]/30 border-2 border-white shadow-md overflow-hidden">
                  <img src={logo} alt="Artie" className="w-full h-full object-cover" />
               </div>
             </div>
             <div className="space-y-1">
               <h1 className="text-2xl font-bold font-cabinet text-[oklch(0.2686_0_0)]">
                  What kind of stories do you tell?
               </h1>
               <p className="text-[oklch(0.55_0_0)] font-ibm text-sm leading-relaxed">
                  I adapt my analysis to your creative workflow. Pick the options that best describe your primary content so I can find the right benchmarks.
               </p>
             </div>
          </div>
        </div>

        {/* Section 1: Medium (Cards) */}
        <div className="w-full mb-10 animate-in slide-in-from-bottom-4 duration-500 delay-100">
          <label className="block text-sm font-semibold text-[oklch(0.2686_0_0)] font-cabinet mb-4 tracking-wide">
            I primarily create...
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Card 1: Short Form */}
            <label className="cursor-pointer group">
              <input 
                type="radio" 
                name="videoForm" 
                className="hidden peer" 
                checked={videoForm === "Vertical Video"}
                onChange={() => setVideoForm("Vertical Video")}
              />
              <div className="peer-checked:border-[oklch(0.70_0.18_55)] peer-checked:bg-[color-mix(in_srgb,oklch(0.70_0.18_55)_5%,oklch(1.0000_0_0))] peer-checked:shadow-[0_4px_12px_color-mix(in_srgb,oklch(0.70_0.18_55)_15%,transparent)] relative p-5 bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] rounded-xl transition-all duration-200 hover:border-[oklch(0.70_0.18_55)]/50 hover:shadow-sm h-full flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[oklch(0.97_0.01_60)] flex items-center justify-center text-[oklch(0.2686_0_0)] group-hover:text-[oklch(0.70_0.18_55)] transition-colors shrink-0">
                  <Smartphone className="w-5 h-5 stroke-[1.5]" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-[oklch(0.2686_0_0)] text-sm font-cabinet">
                      Vertical Video
                    </h3>
                    <div className={`transition-all duration-200 text-[oklch(0.70_0.18_55)] ${videoForm === "Vertical Video" ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}>
                      <CheckCircle2 className="w-5 h-5 fill-[oklch(0.70_0.18_55)] text-[oklch(1.0000_0_0)]" />
                    </div>
                  </div>
                  <p className="text-xs text-[oklch(0.55_0_0)] mt-1 font-ibm leading-normal">
                    TikTok, Reels, Shorts. Vertical & snappy.
                  </p>
                </div>
              </div>
            </label>

            {/* Card 2: Long Form */}
            <label className="cursor-pointer group">
              <input
                type="radio"
                name="videoForm"
                className="hidden peer"
                checked={videoForm === "Long form Video"}
                onChange={() => setVideoForm("Long form Video")}
              />
              <div className="peer-checked:border-[oklch(0.70_0.18_55)] peer-checked:bg-[color-mix(in_srgb,oklch(0.70_0.18_55)_5%,oklch(1.0000_0_0))] peer-checked:shadow-[0_4px_12px_color-mix(in_srgb,oklch(0.70_0.18_55)_15%,transparent)] relative p-5 bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] rounded-xl transition-all duration-200 hover:border-[oklch(0.70_0.18_55)]/50 hover:shadow-sm h-full flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[oklch(0.97_0.01_60)] flex items-center justify-center text-[oklch(0.2686_0_0)] group-hover:text-[oklch(0.70_0.18_55)] transition-colors shrink-0">
                  <MonitorPlay className="w-5 h-5 stroke-[1.5]" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-[oklch(0.2686_0_0)] text-sm font-cabinet">
                      Long form Video
                    </h3>
                    <div className={`transition-all duration-200 text-[oklch(0.70_0.18_55)] ${videoForm === "Long form Video" ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}>
                      <CheckCircle2 className="w-5 h-5 fill-[oklch(0.70_0.18_55)] text-[oklch(1.0000_0_0)]" />
                    </div>
                  </div>
                  <p className="text-xs text-[oklch(0.55_0_0)] mt-1 font-ibm leading-normal">
                    YouTube, Essays, Reviews. Deep dives.
                  </p>
                </div>
              </div>
            </label>

            {/* Card 3: Feature Length Film */}
            <label className="cursor-pointer group">
              <input
                type="radio"
                name="videoForm"
                className="hidden peer"
                checked={videoForm === "Feature Length Film"}
                onChange={() => setVideoForm("Feature Length Film")}
              />
              <div className="peer-checked:border-[oklch(0.70_0.18_55)] peer-checked:bg-[color-mix(in_srgb,oklch(0.70_0.18_55)_5%,oklch(1.0000_0_0))] peer-checked:shadow-[0_4px_12px_color-mix(in_srgb,oklch(0.70_0.18_55)_15%,transparent)] relative p-5 bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] rounded-xl transition-all duration-200 hover:border-[oklch(0.70_0.18_55)]/50 hover:shadow-sm h-full flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[oklch(0.97_0.01_60)] flex items-center justify-center text-[oklch(0.2686_0_0)] group-hover:text-[oklch(0.70_0.18_55)] transition-colors shrink-0">
                  <Clapperboard className="w-5 h-5 stroke-[1.5]" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-[oklch(0.2686_0_0)] text-sm font-cabinet">
                      Feature Length Film
                    </h3>
                    <div className={`transition-all duration-200 text-[oklch(0.70_0.18_55)] ${videoForm === "Feature Length Film" ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}>
                      <CheckCircle2 className="w-5 h-5 fill-[oklch(0.70_0.18_55)] text-[oklch(1.0000_0_0)]" />
                    </div>
                  </div>
                  <p className="text-xs text-[oklch(0.55_0_0)] mt-1 font-ibm leading-normal">
                    Narrative, Indie, Docs. Cinematic.
                  </p>
                </div>
              </div>
            </label>

            {/* Card 4: Podcast */}
            <label className="cursor-pointer group">
              <input
                type="radio"
                name="videoForm"
                className="hidden peer"
                checked={videoForm === "Podcast & Audio"}
                onChange={() => setVideoForm("Podcast & Audio")}
              />
              <div className="peer-checked:border-[oklch(0.70_0.18_55)] peer-checked:bg-[color-mix(in_srgb,oklch(0.70_0.18_55)_5%,oklch(1.0000_0_0))] peer-checked:shadow-[0_4px_12px_color-mix(in_srgb,oklch(0.70_0.18_55)_15%,transparent)] relative p-5 bg-[oklch(1.0000_0_0)] border border-[oklch(0.92_0_0)] rounded-xl transition-all duration-200 hover:border-[oklch(0.70_0.18_55)]/50 hover:shadow-sm h-full flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[oklch(0.97_0.01_60)] flex items-center justify-center text-[oklch(0.2686_0_0)] group-hover:text-[oklch(0.70_0.18_55)] transition-colors shrink-0">
                  <Mic className="w-5 h-5 stroke-[1.5]" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-[oklch(0.2686_0_0)] text-sm font-cabinet">
                      Podcast & Audio
                    </h3>
                    <div className={`transition-all duration-200 text-[oklch(0.70_0.18_55)] ${videoForm === "Podcast & Audio" ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}>
                      <CheckCircle2 className="w-5 h-5 fill-[oklch(0.70_0.18_55)] text-[oklch(1.0000_0_0)]" />
                    </div>
                  </div>
                  <p className="text-xs text-[oklch(0.55_0_0)] mt-1 font-ibm leading-normal">
                    Interviews, storytelling. Audio-first.
                  </p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Section 2: Genre (Pills) */}
        <div className="w-full mb-10 animate-in slide-in-from-bottom-4 duration-500 delay-200">
          <label className="block text-sm font-semibold text-[oklch(0.2686_0_0)] font-cabinet mb-4 tracking-wide">
            My content focuses on...
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              { label: "🎓 Education", value: "Education" },
              { label: "🍿 Entertainment", value: "Entertainment" },
              { label: "🌿 Lifestyle", value: "Lifestyle" },
              { label: "💼 Business", value: "Business" },
              { label: "💻 Tech", value: "Tech" },
              { label: "Other", value: "Other" },
            ].map((item) => (
              <label key={item.value} className="cursor-pointer">
                <input 
                  type="radio" 
                  name="genre" 
                  className="hidden peer"
                  checked={genre === item.value}
                  onChange={() => setGenre(item.value)}
                />
                <span className="peer-checked:bg-[oklch(0.2686_0_0)] peer-checked:text-[oklch(1.0000_0_0)] peer-checked:border-[oklch(0.2686_0_0)] inline-flex items-center px-4 py-2 rounded-full border border-[oklch(0.92_0_0)] bg-[oklch(1.0000_0_0)] text-sm font-medium text-[oklch(0.55_0_0)] hover:border-[oklch(0.2686_0_0)] hover:text-[oklch(0.2686_0_0)] transition-all font-ibm">
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Section 3: Audience (Multi-select) */}
        <div className="w-full mb-8 animate-in slide-in-from-bottom-4 duration-500 delay-300">
          <label className="block text-sm font-semibold text-[oklch(0.2686_0_0)] font-cabinet mb-2 tracking-wide">
            Describe your audience
          </label>
          <p className="text-xs text-[oklch(0.55_0_0)] font-ibm mb-4 leading-normal">
            Make this feel adjustable over time, not permanent. This helps us tailor recommendations and brand matches.
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              "Gen Z",
              "Millennials",
              "Gen X",
              "Boomer",
              "Mixed ages",
              "Family-friendly",
              "Predominantly female",
              "Predominantly male",
              "Non-binary / gender diverse",
              "Global",
              "United States",
              "North America",
              "Europe",
              "Asia-Pacific",
              "Latin America",
              "Urban",
              "Suburban",
              "Rural",
              "Mixed"
            ].map((option) => (
              <button
                key={option}
                onClick={() => toggleAudience(option)}
                className={`
                  group relative flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all duration-200 font-ibm text-left
                  ${audience.includes(option)
                    ? "border-[oklch(0.70_0.18_55)] bg-[color-mix(in_srgb,oklch(0.70_0.18_55)_5%,oklch(1.0000_0_0))] text-[oklch(0.2686_0_0)] shadow-sm"
                    : "border-[oklch(0.92_0_0)] bg-[oklch(1.0000_0_0)] text-[oklch(0.55_0_0)] hover:border-[oklch(0.70_0.18_55)]/50 hover:text-[oklch(0.2686_0_0)]"
                  }
                `}
              >
                <span>{option}</span>
                {audience.includes(option) && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-[oklch(0.70_0.18_55)]" />
                )}
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* Footer Action */}
      <footer className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[oklch(1.0000_0_0)] via-[oklch(1.0000_0_0)] to-transparent z-50 flex justify-center animate-in slide-in-from-bottom-4 duration-500 delay-400">
        <div className="w-full max-w-2xl flex items-center justify-end">
          <button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="group flex items-center gap-2 bg-[oklch(0.2686_0_0)] hover:bg-[oklch(0.2686_0_0)]/90 text-[oklch(1.0000_0_0)] px-8 py-3 rounded-xl text-sm font-medium transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 font-ibm disabled:opacity-70 disabled:hover:translate-y-0"
          >
            <span>{isSubmitting ? "Saving..." : "Continue"}</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </footer>
    </div>
  );
}